import { forwardRef } from 'react';
import './JxIconButton.css';

/**
 * JxIconButton — minimal icon-only button. No border, no pill background —
 * just the icon with a subtle hover/active highlight. Meant for titlebar
 * actions (save/close) and other inline icon controls where a full JxButton
 * (bordered, pill-shaped) would look too heavy sitting side by side.
 *
 * forwardRef is required: MUI's Tooltip clones its child to attach a ref + event
 * handlers. The clone passes className="" on the clone — merge it instead of
 * letting it override jx-icon-button, or the button renders fully unstyled
 * whenever this is wrapped in a Tooltip/JxTooltip.
 *
 * @param {React.ReactNode} icon
 * @param {() => void} onClick
 * @param {string} [title] - native title attribute for a plain hover tooltip; use JxTooltip wrapper for the themed one
 * @param {boolean} [active] - when true, applies jx-icon-button--active class (e.g. pin toggled on)
 * @param {boolean} disabled
 */
const JxIconButton = forwardRef(function JxIconButton({ icon, onClick, title, disabled = false, active = false, className, ...rest }, ref) {
    const cls = ['jx-icon-button', active && 'jx-icon-button--active', className].filter(Boolean).join(' ');

    return (
        <button
            ref={ref}
            type="button"
            className={cls}
            onClick={onClick}
            title={title}
            disabled={disabled}
            {...rest}
        >
            {icon}
        </button>
    );
});

export default JxIconButton;
